import { useEffect, useState } from "react";
import { api, CATEGORIES, imgUrl } from "../lib/api";
import ProductCard from "../components/ProductCard";
import ReviewsSection from "../components/ReviewsSection";
import { Mail, MessageCircle, Sparkles, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1597960194599-22929afc25b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const CATEGORY_CARDS = [
  { key: "bois", label: "Bois", desc: "Chaleur naturelle, gravure profonde", img: "https://images.unsplash.com/photo-1497219055242-93359eeed651?w=800&q=80" },
  { key: "verre", label: "Verre", desc: "Élégance et transparence", img: "https://images.unsplash.com/photo-1629488216876-6360ebd92a36?w=800&q=80" },
  { key: "ardoise", label: "Ardoise", desc: "Minéral, mat, intemporel", img: "https://images.unsplash.com/photo-1711838813592-253710c72e1e?w=800&q=80" },
  { key: "flocage", label: "Flocage", desc: "Textile personnalisé, sport, événementiel", img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=800&q=80" },
];

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({ email: "", whatsapp: "" });

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products${category !== "all" ? `?category=${category}` : ""}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  useEffect(() => {
    api.get("/contact").then((r) => setContact(r.data)).catch(() => {});
  }, []);

  return (
    <main>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-stone-900/55" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 sm:py-32 lg:py-40">
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-stone-50/15 backdrop-blur border border-stone-50/25 text-stone-50 text-xs uppercase tracking-[0.2em] rounded-sm">
            <Sparkles size={14} /> Atelier artisanal — Bidoudou50.fr
          </span>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-stone-50 leading-tight max-w-3xl">
            La gravure et le flocage<br />
            <span className="text-orange-300 italic">sur mesure</span>, à Villedieu-les-Poêles.
          </h1>
          <p className="mt-5 text-stone-200 max-w-xl text-base sm:text-lg leading-relaxed">
            Bois, verre, ardoise, textile floqué — chaque pièce est créée à la main avec soin. Des cadeaux uniques, durables et personnalisés.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              data-testid="hero-cta-shop"
              href="#produits"
              className="px-6 py-3 bg-orange-700 hover:bg-orange-800 text-white font-medium rounded-sm transition-colors"
            >
              Voir la boutique
            </a>
            <a
              data-testid="hero-cta-whatsapp"
              href={`https://wa.me/${contact.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-stone-50/10 hover:bg-stone-50/20 backdrop-blur border border-stone-50/30 text-white font-medium rounded-sm transition-colors inline-flex items-center gap-2"
            >
              <MessageCircle size={16} /> Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Catégories highlight */}
      <section id="categories" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CATEGORY_CARDS.map((c) => (
            <button
              key={c.key}
              data-testid={`category-card-${c.key}`}
              onClick={() => {
                setCategory(c.key);
                document.getElementById("produits")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group text-left bg-white border border-stone-200 rounded-sm overflow-hidden hover:border-orange-700 transition-colors"
            >
              <div className="product-image-wrap aspect-[4/3] bg-stone-100">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <span className="uppercase tracking-[0.18em] text-xs font-semibold text-stone-500">Catégorie</span>
                <h3 className="font-serif text-2xl text-stone-900 mt-1">{c.label}</h3>
                <p className="text-sm text-stone-600 mt-1">{c.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Produits */}
      <section id="produits" className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="uppercase tracking-[0.18em] text-xs font-semibold text-stone-500">La boutique</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-1">Nos créations</h2>
          </div>
          <div data-testid="category-filter" className="flex flex-wrap gap-1.5 bg-stone-100 p-1 rounded-sm">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`category-filter-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${
                  category === c.key
                    ? "bg-stone-900 text-white"
                    : "text-stone-700 hover:bg-stone-200"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-sm h-96 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div data-testid="empty-products" className="text-center py-20">
            <p className="font-serif text-2xl text-stone-500">Aucun produit dans cette catégorie pour le moment.</p>
          </div>
        ) : (
          <div data-testid="products-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Avis clients */}
      <ReviewsSection />

      {/* Contact */}
      <section id="contact" className="bg-stone-900 text-stone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <span className="uppercase tracking-[0.18em] text-xs font-semibold text-stone-400">Contact</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-50 mt-1">Une idée précise en tête ?</h2>
            <p className="mt-4 text-stone-300 max-w-md">
              Envoyez-nous votre demande de gravure ou de flocage personnalisé. Nous répondons sous 24h.
            </p>
            <div data-testid="contact-location" className="mt-6 inline-flex items-center gap-3 px-4 py-3 bg-stone-800/60 border border-stone-700 rounded-sm">
              <MapPin size={18} className="text-orange-300" />
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-stone-400">Atelier</div>
                <div className="font-medium text-stone-50">Villedieu-les-Poêles · 50800</div>
                <div className="text-xs text-stone-400">Manche, Normandie</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 justify-center">
            <a
              data-testid="footer-email-link"
              href={`mailto:${contact.email}`}
              className="inline-flex items-center gap-3 px-5 py-4 bg-stone-800 hover:bg-stone-700 rounded-sm transition-colors"
            >
              <Mail size={20} className="text-orange-300" />
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-stone-400">Email</div>
                <div className="font-medium">{contact.email || "—"}</div>
              </div>
            </a>
            <a
              data-testid="footer-whatsapp-link"
              href={`https://wa.me/${contact.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-5 py-4 bg-[#25D366] hover:bg-[#1ebd57] text-stone-900 rounded-sm transition-colors"
            >
              <MessageCircle size={20} />
              <div>
                <div className="text-xs uppercase tracking-[0.15em] opacity-70">WhatsApp</div>
                <div className="font-bold">+{contact.whatsapp}</div>
              </div>
            </a>
          </div>
        </div>
        <div className="border-t border-stone-800 py-5 text-center text-xs text-stone-500">
          © {new Date().getFullYear()} Bidoudou50.fr — Tous droits réservés
        </div>
      </section>
    </main>
  );
}
